/* eslint-disable no-unused-vars */
/** @module Auth0PostLoginHandlerDefault */
/* eslint-disable import/extensions */
import Auth0PostLoginHandlerBase from '../auth0-post-login-handler-base.mst.mjs';
import Auth0TokenStorageHelper from '../auth0-token-storage-helper.mjs';

/**
 * @class Auth0PostLoginHandlerDefault
 * @description Default post-login handler for applications that don't need specialized
 *              post-login logic. Stores tokens using the provided configuration (or default)
 *              and redirects to the targetUrl from appState.
 *
 *              This handler is used for tenant classes/apps that don't have ShipStation-specific
 *              requirements like multi-partition redirect logic, new user onboarding, etc.
 *
 *              Token storage:
 *              - If tokenStorageConfig is provided, tokens are stored according to that config
 *              - Otherwise, default configuration (_lat and _latv3 cookies) is used
 */
class Auth0PostLoginHandlerDefault extends Auth0PostLoginHandlerBase {
  constructor(options) {
    super(options);
    this._options = options;
    // Custom token storage config passed from the calling app
    this._tokenStorageConfig = options.tokenStorageConfig;
  }

  /**
   * @inheritdoc
   * Default handler never explicitly matches - it's selected when no other handler matches.
   */
  async shouldHandle(loginData) {
    return Promise.resolve(false);
  }

  /**
   * @inheritdoc
   * Stores tokens using provided configuration (or default) and redirects to targetUrl or origin.
   */
  async run(loginData) {
    const appStateTargetUrl = (loginData.appState || {}).targetUrl;
    // Fall back to the targetUrl QS param — present when reached via triggerReLoginWithLinkedAccount,
    // which redirects through the app's /login with ?targetUrl=... and appState comes back empty.
    const qsTargetUrl = new URLSearchParams(window.location.search).get('targetUrl');
    const targetUrl = appStateTargetUrl || qsTargetUrl || null;

    console.log('Auth0PostLoginHandlerDefault.run()...');

    // Store tokens using custom config if provided, otherwise use default
    // - tokenStorageConfig is undefined/null: use default storage (_lat, _latv3 cookies)
    // - tokenStorageConfig is [] (empty array): skip storage entirely (none mode)
    // - tokenStorageConfig has items: use that config
    if (loginData.tokens) {
      if (Array.isArray(this._tokenStorageConfig)) {
        if (this._tokenStorageConfig.length > 0) {
          console.log('Auth0PostLoginHandlerDefault => using custom token storage config:', JSON.stringify(this._tokenStorageConfig));
          Auth0TokenStorageHelper.storeTokens(loginData.tokens, this._tokenStorageConfig);
        } else {
          console.log('Auth0PostLoginHandlerDefault => tokenStorageConfig is empty array, skipping token storage (none mode)');
        }
      } else {
        console.log('Auth0PostLoginHandlerDefault => using default token storage config');
        Auth0TokenStorageHelper.storeTokensDefault(loginData.tokens);
      }
    }

    if (targetUrl) {
      // Validate protocol to prevent javascript:/data: injection via user-controlled qsTargetUrl
      let safeTargetUrl = null;
      try {
        const parsed = new URL(targetUrl, window.location.origin);
        if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
          safeTargetUrl = parsed.href;
        }
      } catch {
        // malformed URL — fall through to origin redirect
      }
      if (safeTargetUrl) {
        console.log(`Auth0PostLoginHandlerDefault => redirecting to targetUrl: ${safeTargetUrl}`);
        window.location.href = safeTargetUrl; // lgtm[js/client-side-unvalidated-url-redirection] — protocol validated above; cross-origin redirects are intentional in post-login flow
      } else {
        console.warn('Auth0PostLoginHandlerDefault => targetUrl rejected (unsafe protocol), redirecting to origin');
        window.location.href = window.location.origin;
      }
    } else {
      console.log('Auth0PostLoginHandlerDefault => no targetUrl, redirecting to origin');
      window.location.href = window.location.origin;
    }
  }
}

export default Auth0PostLoginHandlerDefault;
